import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Search, X } from "lucide-react"
import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { cuppingsPath, newCuppingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface Sample {
  id: number
  name: string
  description: string
  origin: string
  producer: string
}

interface Props {
  existing_samples: Sample[]
}

export default function NewTasting({ existing_samples }: Props) {
  const { t, i18n } = useTranslation()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("cuppings.breadcrumb"),
      href: cuppingsPath(),
    },
    {
      title: t("cuppings.new.page_title"),
      href: newCuppingPath(),
    },
  ]

  const [sessionName, setSessionName] = useState(
    `${t("cuppings.new.default_session_name")} ${new Date().toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" })}`,
  )
  const [scheduledDate, setScheduledDate] = useState(
    new Date().toISOString().slice(0, 16),
  )
  const [protocol, setProtocol] = useState("arabica")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedSamples, setSelectedSamples] = useState<Sample[]>([])
  const [touched, setTouched] = useState({
    name: false,
    date: false,
    samples: false,
  })
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [includeRoastInfo, setIncludeRoastInfo] = useState(false)
  const [roastInfo, setRoastInfo] = useState({
    roaster: "",
    humidity: "",
    density: "",
    waterActivity: "",
    roastProfile: "",
    agtronScale: "",
  })
  const [includeBrewInfo, setIncludeBrewInfo] = useState(false)
  const [brewInfo, setBrewInfo] = useState({
    method: "",
    grindSize: "",
    temperature: "",
    ratio: "",
  })

  const filteredSamples = useMemo(() => {
    // Filter out already selected samples
    const availableSamples = existing_samples.filter(
      (s) => !selectedSamples.some((sel) => sel.id === s.id),
    )
    if (!searchQuery) return availableSamples
    const query = searchQuery.toLowerCase()
    return availableSamples.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.origin?.toLowerCase().includes(query) ||
        s.producer?.toLowerCase().includes(query),
    )
  }, [existing_samples, searchQuery, selectedSamples])

  const handleSelectSample = (sample: Sample) => {
    setSelectedSamples([...selectedSamples, sample])
    setSearchQuery("")
  }

  const handleRemoveSample = (sampleId: number) => {
    setSelectedSamples(selectedSamples.filter((s) => s.id !== sampleId))
  }

  // Validation helpers
  const errors = {
    name: !sessionName.trim(),
    date: !scheduledDate,
    samples: selectedSamples.length === 0,
  }
  const hasErrors = errors.name || errors.date || errors.samples
  const showNameError = errors.name && (touched.name || submitAttempted)
  const showDateError = errors.date && (touched.date || submitAttempted)
  const showSamplesError =
    errors.samples && (touched.samples || submitAttempted)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)

    if (hasErrors) {
      return
    }

    const data = {
      cupping: {
        title: sessionName,
        start_date: scheduledDate,
        cupping_form: protocol,
        // Roast info (only if enabled)
        ...(includeRoastInfo && {
          roaster: roastInfo.roaster,
          humidity: roastInfo.humidity || null,
          density: roastInfo.density || null,
          water_activity: roastInfo.waterActivity || null,
          roast_profile: roastInfo.roastProfile,
          agtron_scale: roastInfo.agtronScale || null,
        }),
        // Brew info (only if enabled)
        ...(includeBrewInfo && {
          brew_method: brewInfo.method,
          grind_size: brewInfo.grindSize || null,
          brew_temperature: brewInfo.temperature || null,
          brew_ratio: brewInfo.ratio,
        }),
        // Selected samples
        samples_attributes: selectedSamples.map((sample) => ({
          id: sample.id,
        })),
      },
    }

    router.post(cuppingsPath(), data)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("cuppings.new.page_title")} />

      <div className="flex h-full flex-1 flex-col gap-4 p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Link href={cuppingsPath()}>
                <ArrowLeft className="text-muted-foreground hover:text-foreground h-5 w-5 cursor-pointer" />
              </Link>
              <div>
                <CardTitle>{t("cuppings.new.title")}</CardTitle>
                <p className="text-muted-foreground text-sm">
                  {t("cuppings.new.description")}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Session Info */}
              <div className="space-y-2">
                <Label
                  htmlFor="sessionName"
                  className={showNameError ? "text-destructive" : ""}
                >
                  {t("cuppings.new.fields.session_name")}
                </Label>
                <Input
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  onBlur={() => setTouched({ ...touched, name: true })}
                  placeholder={t(
                    "cuppings.new.fields.session_name_placeholder",
                  )}
                  className={
                    showNameError
                      ? "border-destructive ring-destructive ring-1"
                      : ""
                  }
                />
                {showNameError && (
                  <p className="text-destructive text-sm">
                    {t("cuppings.new.fields.session_name_required")}
                  </p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label
                    htmlFor="scheduledDate"
                    className={showDateError ? "text-destructive" : ""}
                  >
                    {t("cuppings.new.fields.scheduled_date")}
                  </Label>
                  <Input
                    id="scheduledDate"
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    onBlur={() => setTouched({ ...touched, date: true })}
                    className={
                      showDateError
                        ? "border-destructive ring-destructive ring-1"
                        : ""
                    }
                  />
                  {showDateError && (
                    <p className="text-destructive text-sm">
                      {t("cuppings.new.fields.scheduled_date_required")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="protocol">
                    {t("cuppings.new.fields.protocol")}
                  </Label>
                  <Select value={protocol} onValueChange={setProtocol}>
                    <SelectTrigger id="protocol">
                      <SelectValue
                        placeholder={t(
                          "cuppings.new.fields.protocol_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="arabica">
                        {t("cuppings.new.protocols.arabica")}
                      </SelectItem>
                      <SelectItem value="robusta">
                        {t("cuppings.new.protocols.robusta")}
                      </SelectItem>
                      <SelectItem value="cup_of_excellence">
                        {t("cuppings.new.protocols.cup_of_excellence")}
                      </SelectItem>
                      <SelectItem value="sca_cva_affective">
                        {t("cuppings.new.protocols.sca_cva_affective")}
                      </SelectItem>
                      <SelectItem value="sca_cva_descriptive">
                        {t("cuppings.new.protocols.sca_cva_descriptive")}
                      </SelectItem>
                      <SelectItem value="sca_cva_combined">
                        {t("cuppings.new.protocols.sca_cva_combined")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sample Selector */}
              <div className="space-y-3">
                <Label className={showSamplesError ? "text-destructive" : ""}>
                  {t("cuppings.new.fields.select_samples")}
                </Label>

                {/* Selected Samples */}
                {selectedSamples.length > 0 && (
                  <div className="space-y-2">
                    {selectedSamples.map((sample, index) => (
                      <div
                        key={sample.id}
                        className="bg-primary/5 flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium">{sample.name}</p>
                            <p className="text-muted-foreground text-sm">
                              {[sample.origin, sample.producer]
                                .filter(Boolean)
                                .join(" • ")}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSample(sample.id)}
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search and Add More */}
                <div className="space-y-2">
                  <div
                    className={`relative rounded-lg ${showSamplesError ? "ring-destructive ring-2" : ""}`}
                  >
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setTouched({ ...touched, samples: true })}
                      placeholder={t(
                        "cuppings.new.fields.search_samples_placeholder",
                      )}
                      className={`pl-9 ${showSamplesError ? "border-destructive ring-destructive ring-1" : ""}`}
                    />
                  </div>
                  {showSamplesError && (
                    <p className="text-destructive text-sm">
                      {t("cuppings.new.fields.select_samples_required")}
                    </p>
                  )}
                  {existing_samples.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("cuppings.new.fields.no_samples_available")}{" "}
                      <Link
                        href="/samples/new"
                        className="text-primary hover:underline"
                      >
                        {t("cuppings.new.fields.create_sample_first")}
                      </Link>
                      .
                    </p>
                  ) : filteredSamples.length === 0 && searchQuery ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("cuppings.new.fields.no_samples_found")} "{searchQuery}
                      "
                    </p>
                  ) : filteredSamples.length === 0 &&
                    selectedSamples.length > 0 ? (
                    <p className="text-muted-foreground py-2 text-center text-sm">
                      {t("cuppings.new.fields.all_samples_selected")}
                    </p>
                  ) : filteredSamples.length > 0 ? (
                    <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
                      {filteredSamples.map((sample) => (
                        <button
                          key={sample.id}
                          type="button"
                          onClick={() => handleSelectSample(sample)}
                          className="hover:bg-accent w-full p-3 text-left transition-colors"
                        >
                          <p className="font-medium">{sample.name}</p>
                          <p className="text-muted-foreground text-sm">
                            {[sample.origin, sample.producer]
                              .filter(Boolean)
                              .join(" • ") ||
                              t("cuppings.new.fields.no_additional_info")}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Roast Info Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeRoastInfo"
                  checked={includeRoastInfo}
                  onCheckedChange={(checked) =>
                    setIncludeRoastInfo(checked as boolean)
                  }
                />
                <Label
                  htmlFor="includeRoastInfo"
                  className="cursor-pointer text-sm font-medium"
                >
                  {t("cuppings.new.fields.add_roast_info")}
                </Label>
              </div>

              {/* Roast Info Fields */}
              {includeRoastInfo && (
                <div className="bg-background space-y-4 rounded-lg border p-4">
                  <h4 className="font-medium">
                    {t("cuppings.new.fields.roast_info_title")}
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="roaster">
                        {t("cuppings.new.fields.roaster")}
                      </Label>
                      <Input
                        id="roaster"
                        value={roastInfo.roaster}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            roaster: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.roaster_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="humidity">
                        {t("cuppings.new.fields.humidity")}
                      </Label>
                      <Input
                        id="humidity"
                        type="number"
                        step="0.1"
                        value={roastInfo.humidity}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            humidity: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.humidity_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="density">
                        {t("cuppings.new.fields.density")}
                      </Label>
                      <Input
                        id="density"
                        type="number"
                        value={roastInfo.density}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            density: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.density_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="waterActivity">
                        {t("cuppings.new.fields.water_activity")}
                      </Label>
                      <Input
                        id="waterActivity"
                        type="number"
                        step="0.01"
                        value={roastInfo.waterActivity}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            waterActivity: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.water_activity_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="roastProfile">
                        {t("cuppings.new.fields.roast_profile")}
                      </Label>
                      <Select
                        value={roastInfo.roastProfile}
                        onValueChange={(value) =>
                          setRoastInfo({ ...roastInfo, roastProfile: value })
                        }
                      >
                        <SelectTrigger id="roastProfile">
                          <SelectValue
                            placeholder={t(
                              "cuppings.new.fields.roast_profile_placeholder",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">
                            {t("cuppings.new.roast_profiles.light")}
                          </SelectItem>
                          <SelectItem value="medium">
                            {t("cuppings.new.roast_profiles.medium")}
                          </SelectItem>
                          <SelectItem value="dark">
                            {t("cuppings.new.roast_profiles.dark")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agtronScale">
                        {t("cuppings.new.fields.agtron_scale")}
                      </Label>
                      <Input
                        id="agtronScale"
                        type="number"
                        value={roastInfo.agtronScale}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            agtronScale: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.agtron_scale_placeholder",
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Brew Info Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeBrewInfo"
                  checked={includeBrewInfo}
                  onCheckedChange={(checked) =>
                    setIncludeBrewInfo(checked as boolean)
                  }
                />
                <Label
                  htmlFor="includeBrewInfo"
                  className="cursor-pointer text-sm font-medium"
                >
                  {t("cuppings.new.fields.add_brew_info")}
                </Label>
              </div>

              {/* Brew Info Fields */}
              {includeBrewInfo && (
                <div className="bg-background space-y-4 rounded-lg border p-4">
                  <h4 className="font-medium">
                    {t("cuppings.new.fields.brew_info_title")}
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="method">
                        {t("cuppings.new.fields.brew_method")}
                      </Label>
                      <Input
                        id="method"
                        value={brewInfo.method}
                        onChange={(e) =>
                          setBrewInfo({ ...brewInfo, method: e.target.value })
                        }
                        placeholder={t(
                          "cuppings.new.fields.brew_method_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grindSize">
                        {t("cuppings.new.fields.grind_size")}
                      </Label>
                      <Input
                        id="grindSize"
                        type="number"
                        value={brewInfo.grindSize}
                        onChange={(e) =>
                          setBrewInfo({
                            ...brewInfo,
                            grindSize: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.grind_size_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="temperature">
                        {t("cuppings.new.fields.temperature")}
                      </Label>
                      <Input
                        id="temperature"
                        type="number"
                        value={brewInfo.temperature}
                        onChange={(e) =>
                          setBrewInfo({
                            ...brewInfo,
                            temperature: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.new.fields.temperature_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ratio">
                        {t("cuppings.new.fields.ratio")}
                      </Label>
                      <Input
                        id="ratio"
                        value={brewInfo.ratio}
                        onChange={(e) =>
                          setBrewInfo({ ...brewInfo, ratio: e.target.value })
                        }
                        placeholder={t("cuppings.new.fields.ratio_placeholder")}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={selectedSamples.length === 0 || !sessionName.trim()}
                >
                  {t("cuppings.new.actions.create")}
                </Button>
                <Link href={cuppingsPath()}>
                  <Button type="button" variant="outline" size="sm">
                    {t("cuppings.new.actions.cancel")}
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
